// ===================================
// TOPICS API SERVICE - src/services/topicsApi.ts
// ===================================
import api from './api';
import {
  Topic,
  Action,
  CreateTopicRequest,
  UpdateTopicRequest,
  CreateActionRequest,
  PaginatedResponse,
  ApiResponse,
  ListTopicsParams,
  ListActionsParams
} from '../types';

export class TopicsApi {
  
  // ===================================
  // TOPICS CRUD OPERATIONS
  // ===================================

  /**
   * Get list of topics with pagination and filtering
   */
  static async getTopics(params: ListTopicsParams = {}): Promise<PaginatedResponse<Topic>> {
    const searchParams = new URLSearchParams();
    
    if (params.business_id) searchParams.append('business_id', params.business_id.toString());
    if (params.q) searchParams.append('q', params.q);
    if (params.page) searchParams.append('page', params.page.toString());
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.sort_by) searchParams.append('sort_by', params.sort_by);
    if (params.sort_order) searchParams.append('sort_order', params.sort_order);

    const response = await api.get(`/topics?${searchParams.toString()}`);
    return response.data;
  }

  /**
   * Get single topic by ID
   */
  static async getTopic(id: number): Promise<Topic> {
    const response = await api.get(`/topics/${id}`);
    return response.data.data || response.data;
  }

  /**
   * Create new topic
   */
  static async createTopic(data: CreateTopicRequest): Promise<Topic> {
    const response = await api.post('/topics', data);
    return response.data.data;
  }

  /**
   * Update existing topic
   */
  static async updateTopic(id: number, data: UpdateTopicRequest): Promise<Topic> {
    const response = await api.put(`/topics/${id}`, data);
    return response.data.data;
  }

  /**
   * Delete topic
   */
  static async deleteTopic(id: number): Promise<void> {
    await api.delete(`/topics/${id}`);
  }

  // ===================================
  // ACTIONS OPERATIONS (for future phases)
  // ===================================

  /**
   * Get actions for a specific topic
   */
  static async getTopicActions(params: ListActionsParams): Promise<PaginatedResponse<Action>> {
    const { topic_id, ...queryParams } = params;
    const searchParams = new URLSearchParams();
    
    if (queryParams.q) searchParams.append('q', queryParams.q);
    if (queryParams.page) searchParams.append('page', queryParams.page.toString());
    if (queryParams.limit) searchParams.append('limit', queryParams.limit.toString());
    if (queryParams.sort_by) searchParams.append('sort_by', queryParams.sort_by);
    if (queryParams.sort_order) searchParams.append('sort_order', queryParams.sort_order);

    const response = await api.get(`/topics/${topic_id}/actions?${searchParams.toString()}`);
    return response.data;
  }

  /**
   * Create action under a topic
   */
  static async createAction(data: CreateActionRequest): Promise<Action> {
    const { topic_id, ...actionData } = data;
    const response = await api.post(`/topics/${topic_id}/actions`, actionData);
    return response.data.data;
  }

  /**
   * Update action
   */
  static async updateAction(topicId: number, actionId: number, data: Partial<CreateActionRequest>): Promise<Action> {
    const response = await api.put(`/topics/${topicId}/actions/${actionId}`, data);
    return response.data.data;
  }

  /**
   * Delete action
   */
  static async deleteAction(topicId: number, actionId: number): Promise<void> {
    await api.delete(`/topics/${topicId}/actions/${actionId}`);
  }

  // ===================================
  // UTILITY METHODS
  // ===================================

  /**
   * Search topics across all accessible businesses
   */
  static async searchTopics(query: string, limit: number = 10): Promise<Topic[]> {
    const response = await this.getTopics({ q: query, limit });
    return response.items;
  }

  /**
   * Get topics count by business
   */
  static async getTopicsCount(business_id?: number): Promise<number> {
    const response = await this.getTopics({ business_id, limit: 1 });
    return response.total;
  }

  /**
   * Get recent topics
   */
  static async getRecentTopics(limit: number = 5): Promise<Topic[]> {
    const response = await this.getTopics({ 
      sort_by: 'updated_at', 
      sort_order: 'desc', 
      limit 
    });
    return response.items;
  }

  /**
   * Get topics with most actions
   */
  static async getPopularTopics(limit: number = 5): Promise<Topic[]> {
    const response = await this.getTopics({ 
      sort_by: 'action_count', 
      sort_order: 'desc', 
      limit 
    });
    return response.items;
  }

  // ===================================
  // BULK OPERATIONS (for future CSV import/export)
  // ===================================

  /**
   * Bulk create topics
   */
  static async bulkCreateTopics(topics: CreateTopicRequest[]): Promise<Topic[]> {
    // For now, create them one by one
    // In the future, this could be optimized with a bulk endpoint
    const results: Topic[] = [];
    
    for (const topicData of topics) {
      try {
        const topic = await this.createTopic(topicData);
        results.push(topic);
      } catch (error) {
        console.error('Failed to create topic:', topicData.name, error);
        // Continue with next topic
      }
    }
    
    return results;
  }

  /**
   * Export topics to CSV-ready format
   */
  static async exportTopics(params: ListTopicsParams = {}): Promise<Topic[]> {
    // Get all topics (remove pagination for export)
    const allTopicsParams = { ...params, limit: 1000 }; // reasonable limit
    const response = await this.getTopics(allTopicsParams);
    return response.items;
  }

  // ===================================
  // ERROR HANDLING HELPERS
  // ===================================

  /**
   * Handle API errors consistently
   */
  static handleApiError(error: any): Error {
    if (error.response?.data?.error) {
      return new Error(error.response.data.error);
    }
    if (error.response?.data?.message) {
      return new Error(error.response.data.message);
    }
    if (error.message) {
      return new Error(error.message);
    }
    return new Error('An unexpected error occurred');
  }

  /**
 * Get topics count for dashboard
 */
// static async getTopicsCount(business_id?: number): Promise<number> {
//   const response = await this.getTopics({ 
//     business_id, 
//     limit: 1000 // Get all for counting
//   });
//   return response.total;
// }

/**
 * Get dashboard statistics
 */
static async getDashboardStats(business_id?: number): Promise<{
  totalTopics: number;
  totalActions: number;
  recentTopics: Topic[];
  popularTopics: Topic[];
}> {
  const [all, recent, popular] = await Promise.all([
    this.getTopics({ business_id, limit: 1000 }),
    this.getTopics({ business_id, limit: 5, sort_by: 'updated_at', sort_order: 'desc' }),
    this.getTopics({ business_id, limit: 5, sort_by: 'action_count', sort_order: 'desc' })
  ]);

  const totalActions = all.items.reduce((sum, topic) => sum + topic.action_count, 0);

  return {
    totalTopics: all.total,
    totalActions,
    recentTopics: recent.items,
    popularTopics: popular.items
  };
}
}
 

// Export default instance for convenience
export default TopicsApi;